import { useCallback } from "react";
import { Resetter, useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";

import { ProductId } from "@settings/products";

import { Result } from "@organisms/result/domain/result";
import { useResult, useUpdateResult } from "@organisms/result/use-cases/result-use-cases";

import { Product } from "@features/quotes-funnel/domain/product";
import { ShoppingCart, ShoppingCartDTO } from "@features/quotes-funnel/domain/shopping-cart";
import { PaymentIntervalMonths } from "@features/quotes-funnel/settings/payments";
import { shoppingCartState } from "@features/quotes-funnel/state/shopping-cart-state";

export function useShoppingCart(): ShoppingCart {
  const shoppingCart: ShoppingCartDTO = useRecoilValue(shoppingCartState);

  return new ShoppingCart(shoppingCart);
}

export function useSetShoppingCart(): (changes: Partial<ShoppingCartDTO>) => void {
  const shoppingCartSetter = useSetRecoilState(shoppingCartState);

  return useCallback(
    (changes: Partial<ShoppingCartDTO>) => {
      shoppingCartSetter((prevCart) => ({
        ...prevCart,
        ...changes,
      }));
    },
    [shoppingCartSetter]
  );
}

export function useSelectHealthProduct(): (productId: ProductId) => void {
  const cart: ShoppingCart = useShoppingCart();
  const updateCart = useSetShoppingCart();
  const selectProduct = (product: Product): Product => {
    return new Product({
      ...product,
      selected: true,
    });
  };

  const unselectProduct = (product: Product): Product => {
    const unselectableProducts = [ProductId.healthBasic, ProductId.healthUnlimited];

    if (unselectableProducts.includes(product.id)) {
      return new Product({
        ...product,
        selected: false,
      });
    }

    return product;
  };

  return useCallback(
    (productId: ProductId) => {
      const products: Product[] = cart.products.map((product) => {
        if (product.id === productId) {
          return selectProduct(product);
        }

        return unselectProduct(product);
      });

      updateCart({ products });
    },
    [cart, updateCart]
  );
}

export function useToggleProduct(): (productId: ProductId) => void {
  const cart = useShoppingCart();
  const updateCart = useSetShoppingCart();

  return (productId: ProductId): void => {
    const products = cart.products.map((product) => {
      if (product.id === productId) {
        return new Product({
          ...product,
          selected: !product.selected,
        });
      }

      return product;
    });

    updateCart({ products });
  };
}

export function useTogglePaymentIntervalMonths(
  setSelected: (value: string) => void
): (value: string) => void {
  const updateCart = useSetShoppingCart();

  return (value: string): void => {
    const paymentIntervalMonths = parseInt(value, 10) as PaymentIntervalMonths;

    setSelected(value);
    updateCart({ paymentIntervalMonths });
  };
}

export function usePersistCart(): () => Promise<Result> | null {
  const result = useResult();
  const updateResult = useUpdateResult();
  const cart = useShoppingCart();

  return useCallback(() => {
    if (!updateResult || !result) {
      return null;
    }

    const updatedQuotes = result.quotes.map((quote) => {
      const cartProduct = cart.products.find((product) => product.id === quote.product.id);
      if (!cartProduct) {
        return quote;
      }

      return { ...quote, selected: cartProduct.selected };
    });

    const newResult = {
      quotes: updatedQuotes,
      payment_interval_months: cart.paymentIntervalMonths,
      policy_start_date: result.policy_start_date,
    };

    return updateResult(newResult);
  }, [updateResult, result, cart.products]);
}

export const useResetShoppingCart = (): (() => void) => {
  const resetCart: Resetter = useResetRecoilState(shoppingCartState);

  return useCallback(() => {
    resetCart();
  }, [resetCart]);
};
